import { useId, useRef, useState } from "react";
import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { ChevronLeftIcon } from "../../shared/components/icons";
import { ChevronRightIcon } from "../../shared/components/icons";
import { CloseIcon } from "../../shared/components/icons";
import { ZoomInIcon } from "../../shared/components/icons";
import { ZoomOutIcon } from "../../shared/components/icons";
import { useT } from "../../shared/i18n";

/** One photo as the lightbox sees it — independent of where it lives (container file or item photo). */
export interface LightboxPhoto {
  id: string;
  name: string;
  src: string;
}

interface PhotoLightboxProps {
  open: boolean;
  photos: LightboxPhoto[];
  /** Index into `photos` of the photo to show. */
  index: number;
  onClose: () => void;
  onIndexChange: (index: number) => void;
  /** data-testid for the dialog root (distinguishes container vs item lightbox). */
  testId: string;
  /** Override the header label (default: "<name> — <i> / <n>"). */
  headerText?: string;
  /** data-testid for the image element (default: "lightbox-image"). */
  imageTestId?: string;
  /** Optional footer content, rendered in the bottom info bar. */
  footer?: ReactNode;
}

/**
 * Full-screen photo lightbox (shared by container media and item photos):
 * loads the ORIGINAL photo (not the preview — originals stay lazy, ticket 21)
 * and offers prev/next navigation, a zoom toggle and horizontal swipe.
 *
 * @param open whether the dialog is visible.
 * @param photos the photos to navigate.
 * @param index index into `photos` of the photo to show.
 * @param onClose called when the dialog closes.
 * @param onIndexChange called with the new index on prev/next/swipe.
 * @param testId data-testid for the dialog root.
 * @param headerText optional header override (default: "<name> — <i> / <n>").
 * @param imageTestId optional image test id (default: "lightbox-image").
 * @param footer optional footer content rendered in the bottom info bar.
 */
export function PhotoLightbox({ open, photos, index, onClose, onIndexChange, testId, headerText, imageTestId, footer }: PhotoLightboxProps) {
  const t = useT();
  const titleId = useId();
  const [zoom, setZoom] = useState(false);
  const touchX = useRef<number | null>(null);
  const photo = photos[index];

  const step = (delta: number) => {
    if (photos.length === 0) return;
    const next = (index + delta + photos.length) % photos.length;
    onIndexChange(next);
    setZoom(false);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchX.current = e.touches[0]?.clientX ?? null;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchX.current === null) return;
    const dx = (e.changedTouches[0]?.clientX ?? touchX.current) - touchX.current;
    touchX.current = null;
    // Swipe past the threshold; ignore the rest so the scroll gesture wins.
    if (Math.abs(dx) > 48) step(dx < 0 ? 1 : -1);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      data-testid={testId}
      aria-labelledby={titleId}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 1,
          background: "rgba(0,0,0,0.55)",
          color: "white",
        }}
      >
        <IconButton aria-label={t("close")} onClick={onClose} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
        <Typography id={titleId} variant="body2" sx={{ flexGrow: 1 }}>
          {headerText ?? (photo ? `${photo.name} — ${index + 1} / ${photos.length}` : "")}
        </Typography>
        <Tooltip title={zoom ? t("lightboxZoomOut") : t("lightboxZoomIn")}>
          <IconButton aria-label={t("lightboxZoom")} onClick={() => setZoom((z) => !z)} sx={{ color: "white" }}>
            {zoom ? <ZoomOutIcon /> : <ZoomInIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {photos.length > 1 && (
        <>
          <IconButton
            aria-label={t("lightboxPrev")}
            onClick={() => step(-1)}
            sx={{
              position: "absolute",
              left: 4,
              top: "50%",
              transform: "translateY(-50%)",
              color: "white",
              background: "rgba(0,0,0,0.55)",
              "&:hover": { background: "rgba(0,0,0,0.75)" },
              zIndex: 1,
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            aria-label={t("lightboxNext")}
            onClick={() => step(1)}
            sx={{
              position: "absolute",
              right: 4,
              top: "50%",
              transform: "translateY(-50%)",
              color: "white",
              background: "rgba(0,0,0,0.55)",
              "&:hover": { background: "rgba(0,0,0,0.75)" },
              zIndex: 1,
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </>
      )}

      <DialogContent
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "auto",
          p: 0,
          bgcolor: "black",
        }}
      >
        {photo && (
          <Box
            component="img"
            src={photo.src}
            alt={photo.name}
            data-testid={imageTestId ?? "lightbox-image"}
            sx={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              cursor: zoom ? "zoom-out" : "zoom-in",
              transform: zoom ? "scale(1.6)" : "none",
              transformOrigin: "center center",
              transition: "transform 200ms ease",
            }}
            onClick={() => setZoom((z) => !z)}
          />
        )}
      </DialogContent>

      {footer && (
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            p: 1,
            background: "rgba(0,0,0,0.55)",
            color: "white",
          }}
        >
          {footer}
        </Box>
      )}
    </Dialog>
  );
}